/**
 * Labstep
 *
 * @module grid/SmartTable/coldefs/metadata/date
 * @desc Column Definition for metadata date
 */

import type {
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { isValid, parse } from 'date-fns';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Icon } from 'labstep-web/core/Icon';
import { BatchCreateResourceItemsDateHeader } from 'labstep-web/grid/BatchCreateResourceItems/components/ColumnHeader/DateHeader';
import {
  ENTITY_DATE_FORMATS,
  GridBatchCreateResourceItemsService,
} from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { Resource } from 'labstep-web/models/resource.model';

export const getMetadataDateColDef = (
  metadata: Metadata,
  resource: Resource,
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
  dateFormats: Record<string, string>,
): BatchCreateResourceItemsColDef => {
  const colId = `metadata:${MetadataType.date}:${metadata.guid}`;

  const setMetadataDate = (
    params: ValueSetterParams,
    newValue: string | null,
    format: string = ENTITY_DATE_FORMATS[0],
  ) => {
    if (!params.node) {
      return false;
    }

    const row = params.node.data;
    if (
      (newValue === null || newValue === '') &&
      metadata.is_required
    ) {
      GridBatchCreateResourceItemsService.insertErrorInData(
        data,
        setData,
        params,
        `This field is required.`,
      );
      return false;
    }

    if (newValue === null || newValue === '') {
      row[colId] = null;
      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    }

    const date = parse(newValue, format, new Date());
    if (isValid(date)) {
      row[colId] =
        GridBatchCreateResourceItemsService.getMetadataBody(
          { date: newValue },
          metadata,
        );
      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    }

    GridBatchCreateResourceItemsService.insertErrorInData(
      data,
      setData,
      params,
      `Wrong format. Date format should be ${format}`,
    );
    return false;
  };

  return {
    colId,
    headerName: metadata.label || '',
    isRequired: metadata.is_required ?? false,
    headerComponent: BatchCreateResourceItemsDateHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.date,
      cornerIcon: ICONS.resource_item.primary,
      data: data,
      setData: setData,
      resource: resource,
    },
    editable: true,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node?.data[colId]) {
        return null;
      }

      return params.node.data[colId].date;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const format = dateFormats[colId] || ENTITY_DATE_FORMATS[0];
      return setMetadataDate(
        params,
        params.newValue as string,
        format,
      );
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      const metadataDate =
        params.node.data[colId]?.date || metadata.date;
      if (!metadataDate) {
        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <DataGridPlaceholder
              params={params}
              editable={true}
              placeholder={'Enter'}
            />
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </span>
        );
      }

      return (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {metadataDate}
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </span>
      );
    },
    valueClearer: () => null,
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
  };
};
